<template>
  <v-dialog
    :value="open"
    scrollable
    persistent
    no-click-animation
    transition="dialog-transition"
    :content-class="$style.dialog"
  >
    <v-card>
      <v-toolbar color="grey-lighten-1 flex-grow-0">
        <v-toolbar-title class="d-flex align-center">
          {{ $t('general.attention') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon :title="$t('general.close')" @click="$emit('hide-dialog')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pt-4">
        <BaseAlert
          type="warning"
          icon="info"
          outlined
          :collapsible-alert="false"
          :initially-open="true"
        >
          <template #message>
            <div v-html="baseAlertMessage"></div>
          </template>
        </BaseAlert>
        <v-row v-if="availableBrands.length > 1" justify="center">
          <v-checkbox
            v-for="radio in availableBrands"
            :key="radio.value"
            v-model="selectedBrands"
            class="mb-4 mr-8"
            :value="radio.value"
            :disabled="radio.disabled"
            :checked="radio.disabled"
          >
            <template #label
              ><BrandLogoRef :brand="radio.brandPimId" :class="$style.brand" :title="radio.label"
            /></template>
          </v-checkbox>
        </v-row>
      </v-card-text>
      <v-card-actions :class="$style.actions">
        <v-row justify="center">
          <v-btn
            type="button"
            class="mx-2 btn"
            color="primary"
            outlined
            rounded
            :elevation="0"
            :title="$t('form.confirmText.confirm')"
            @click="confirm()"
          >
            {{ $t('form.confirmText.confirm') }}
          </v-btn>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from '@/js/helper/axios-helper.js'
import { types as STOREMODULES } from '@/js/plugins/store/store-modules.js'
import APP_GETTERS from '@/js/plugins/store/modules/app/getter-types.js'
import { mapGetters } from 'vuex'
import AIMEOS_GETTERS from '@/js/plugins/store/modules/aimeos/getter-types/index.js'
import { camelCase } from 'change-case'
import BrandLogoRef from '@/js/components/Shared/BrandLogoRef.vue'

export default {
  name: 'BackorderEditDialog',
  components: { BrandLogoRef },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    contextBrand: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      dialog: false,
      fetching: false,
      selectedBrands: this.contextBrand ? [this.contextBrand] : [],
      companyCodesInQueue: this.companyCodesInQueue ?? [],
    }
  },
  computed: {
    ...mapGetters(STOREMODULES.AIMEOS, {
      blockingreasonObjects: AIMEOS_GETTERS.BLOCKINGREASONS_LIST,
    }),
    ...mapGetters(STOREMODULES.APP, {
      muleApiUrl: APP_GETTERS.MULEAPISERVICE_URL,
    }),
    ...mapGetters(STOREMODULES.APP, {
      companies: APP_GETTERS.USER_COMPANIES,
    }),
    allCompanyCodesInQueue() {
      if (this.contextBrand) {
        return this.companyCodesInQueue.includes(this.contextBrand)
      }

      const sortedCompanyCodesWithBlockingReasons = [...this.companyCodesWithBlockingReasons].sort()
      const sortedCompanyCodesInQueue = [...this.companyCodesInQueue].sort()

      const areEqual =
        sortedCompanyCodesWithBlockingReasons.length === sortedCompanyCodesInQueue.length &&
        sortedCompanyCodesWithBlockingReasons.every(
          (code, index) => code === sortedCompanyCodesInQueue[index],
        )

      this.$emit('all-company-codes-in-queue', areEqual)

      return areEqual
    },
    availableBrands() {
      if (this.contextBrand) {
        const brand = this.brands.find(brand => brand.id === this.contextBrand)

        if (brand) {
          return [this.formatBrand(brand)]
        }
      }

      return this.brands.map(brand => this.formatBrand(brand))
    },
    brands() {
      return this.companies.filter(company =>
        this.companyCodesWithBlockingReasons.includes(company.id),
      )
    },
    companyCodesWithBlockingReasons() {
      return this.filteredBlockingreasons.map(item => item.salesOrg)
    },
    baseAlertMessage() {
      let message = this.$t('account.backorderList.backorderEditModal.message')
      return message
    },
    postBackorderUrl() {
      return this.muleApiUrl + '/api/backorderedittaskqueue'
    },
    getBackorderUrl() {
      return this.muleApiUrl + '/api/getbackorderentryqueue'
    },
    filteredBlockingreasons() {
      return this.blockingreasonObjects.filter(blockingreasonObject =>
        blockingreasonObject.blockingReasons.includes('VR'),
      )
    },
    hasBlockingReasons() {
      return this.filteredBlockingreasons.length > 0
    },
  },
  watch: {
    companyCodesInQueue(newVal, oldVal) {
      this.$emit('all-company-codes-in-queue', this.allCompanyCodesInQueue)
    },
    companyCodesWithBlockingReasons(newVal, oldVal) {
      this.$emit('all-company-codes-in-queue', this.allCompanyCodesInQueue)
    },
  },
  mounted() {
    this.checkBackorderEditExists()
    window.addEventListener('backorder-task-created', this.handleCustomEvent)
  },
  beforeDestroy() {
    window.removeEventListener('backorder-task-created', this.handleCustomEvent)
  },
  methods: {
    formatBrand(brand) {
      return {
        value: brand.id,
        label: this.$t(`filter.items.ItemBrand.title.${brand.name}`),
        brandPimId: this.$t(`settings.brand.nameToPimId.${camelCase(brand.name).toLowerCase()}`),
        disabled: this.companyCodesInQueue.includes(brand.id),
      }
    },
    submitBackorderDone() {
      if (this.availableBrands.length === 1) {
        this.selectedBrands = [this.availableBrands[0].value]
      }

      if (this.fetching || this.selectedBrands.length === 0) {
        return
      }

      this.fetching = true

      const postData = {
        selectedBrands: this.selectedBrands,
      }

      axios
        .post(this.postBackorderUrl, postData)
        .then(result => {
          this.fetching = false
          console.warn(result)

          this.triggerDomEvent(postData.selectedBrands)
          this.checkBackorderEditExists()
        })
        .catch(error => {
          this.fetching = false
          console.error(error)
        })
    },
    checkBackorderEditExists() {
      axios
        .get(this.getBackorderUrl)
        .then(result => {
          console.warn(result)
          if (result.status == 200) {
            result?.data?.data.forEach(element => {
              if (!this.companyCodesInQueue.includes(element.companyCode)) {
                this.companyCodesInQueue.push(element.companyCode)
              }
              if (!this.selectedBrands.includes(element.companyCode)) {
                this.selectedBrands.push(element.companyCode)
              }
            })
          }
        })
        .catch(error => {
          console.error(error)
        })
    },
    confirm() {
      this.submitBackorderDone()
      this.$emit('hide-dialog')
    },
    triggerDomEvent(companyCode) {
      const event = new CustomEvent('backorder-task-created', {
        detail: { data: companyCode },
      })
      window.dispatchEvent(event)
    },
    handleCustomEvent(event) {
      console.log('Event received with data:', event.detail.data)
      if (event.detail.data) {
        if (!this.companyCodesInQueue.includes(event.detail.data[0])) {
          this.companyCodesInQueue.push(event.detail.data[0])
        }
        if (!this.selectedBrands.includes(event.detail.data[0])) {
          this.selectedBrands.push(event.detail.data[0])
        }
        this.checkBackorderEditExists()
      }
    },
  },
}
</script>

<style lang="scss" module>
.editIcon {
  margin-right: 0.5rem;
}

.dialog {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    max-width: 600px;
    height: auto;
    max-height: 90% !important;
    margin: 24px;
  }
  height: 100%;
  max-height: 100% !important;
  margin: 0;
}

.actions {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-top: 20px !important;
  padding-bottom: 20px !important;

  box-shadow: 0px 4px 5px 6px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14),
    0px 1px 10px 0px rgba(0, 0, 0, 0.12), 0px 2px 4px -1px rgba(0, 0, 0, 0.2);
}

.brand {
  order: 1;
  width: 9rem;
}
</style>
